import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSettings } from '../context/SettingsContext'
import { DAPPS_LIST } from '../features/settings/components/dapps-panel/dappsList'

// frontend-restructuring §1a — the route→shell derivation that used to be ~50 lines at the top
// of `Home.jsx`. Pure function of `useLocation` / `useParams` / `useSettings().activeSetting`
// (plus one effect: the dapp-view "keep the previous screen" ref). Returns the flags the shell
// + the dispatch read.
//
// This is the seam Phase 1b splits along: today the `AppShell` reads `screen` and renders the
// right panel itself; per feature, `screen === 'tasks'` etc. becomes `<Route path="task/*"
// element={<TasksScreenContainer/>}>` and this flag disappears.

// `logicalPathname` of a dapp route → its `DAPPS_LIST` id. `/dapps` (the panel, not a viewer)
// is deliberately absent — it resolves to `activeDapp: null`.
const DAPP_ID_BY_PATH = {
  '/pdfeditor': 'pdf_editor',
  '/walletsmanager': 'wallet_manager',
  '/iaatomat': 'ai_automation',
  '/docusafe': 'sign_file',
  '/connetgoogle': 'google_calendar',
  '/notion': 'notion',
  '/imapmail': 'imap_mail',
  '/exchange': 'exchange',
  '/spei': 'spei',
  '/facturador': 'billing',
  '/boo': 'boocity',
  '/whats': 'whatsapp',
  '/cosmicphone': 'cosmic_phone',
  '/virtualphone': 'virtual_phone',
  '/jorgebot': 'socialmedia_bot',
}
const DAPP_PATHS = Object.keys(DAPP_ID_BY_PATH)

/** The `logicalPathname` value → which screen the left rail / Menu2 dispatch renders. */
function screenFor(effectivePath, logicalPathname, activeSetting) {
  if (activeSetting !== null) return 'settings'
  if (effectivePath.startsWith('/profile')) return 'profile'
  if (effectivePath.startsWith('/database/user/') || effectivePath.startsWith('/user/')) return 'userProfile'
  if (effectivePath.startsWith('/docs')) return 'docs'
  if (effectivePath.startsWith('/calendar')) return 'calendar'
  if (
    effectivePath.startsWith('/database') ||
    ['/clientes', '/views', '/recordings', '/semantic', '/payment'].includes(effectivePath)
  ) return 'database'
  if (logicalPathname.startsWith('/task')) return 'tasks'
  return 'home'
}

export function useAppShellRoute() {
  const location = useLocation()
  const params = useParams()
  const { activeSetting } = useSettings()
  const { spaceSlug } = params

  const rawPathname = location.pathname
  const decodedRawPathname = decodeURIComponent(rawPathname)
  let logicalPathname = decodedRawPathname
  if (spaceSlug && decodedRawPathname.startsWith(`/${spaceSlug}`)) {
    logicalPathname = decodedRawPathname.substring(spaceSlug.length + 1) || '/'
  }

  const isDappView = logicalPathname === '/dapps' || DAPP_PATHS.includes(logicalPathname)

  // Dapp views overlay the previous screen instead of replacing it — remember the last
  // non-dapp path so the underlying content stays mounted.
  const previousPathRef = useRef(logicalPathname)
  useEffect(() => {
    if (!isDappView) previousPathRef.current = logicalPathname
  }, [logicalPathname, isDappView])

  // Antes: `/all/<dapp>` se redirigía a `/frqtal/<dapp>` con un slug hardcodeado, asumiendo que
  // todos son miembros de un espacio llamado "frqtal". Para quien no lo es, `SpaceLayout` no
  // resolvía ese slug y rebotaba de vuelta a `/all` (la dapp se abría y se cerraba sola). Las
  // dapps (mail, whatsapp, wallet, …) leen el espacio de `user.currentSpace` del store, no del
  // slug de la URL, así que abren bien bajo `/all` con `currentSpace` en null.

  const effectivePath = isDappView ? previousPathRef.current : logicalPathname
  const prefix = spaceSlug ? `/${spaceSlug}` : '/all'

  return useMemo(() => {
    const isDocs = effectivePath.startsWith('/docs')
    const isCalendar = effectivePath.startsWith('/calendar')
    const isDatabaseUserProfile = effectivePath.startsWith('/database/user/')
    const isPublicUserProfile = effectivePath.startsWith('/user/')
    const isDatabase =
      effectivePath.startsWith('/database') ||
      ['/clientes', '/views', '/recordings', '/semantic', '/payment'].includes(effectivePath)
    const isTask = logicalPathname.startsWith('/task')
    // The dapp shown in `DappViewer`, or null. Pure function of the URL — was a `Home` effect
    // that only ever *set* it (never cleared on navigate-away, so a dapp panel stuck around on
    // top of the next screen). `/dapps` (the panel) → null.
    const dappId = DAPP_ID_BY_PATH[logicalPathname] || null
    const activeDapp = dappId ? (DAPPS_LIST?.find((d) => d.id === dappId) || { id: dappId }) : null
    return {
      // raw
      logicalPathname,
      effectivePath,
      prefix,
      params,
      activeDapp,
      // flags the dispatch + chrome branch on
      isDappView,
      isProfile: effectivePath.startsWith('/profile'),
      isDatabaseUserProfile,
      isPublicUserProfile,
      isUserProfile: isDatabaseUserProfile || isPublicUserProfile,
      isDocs,
      isCalendar,
      isDatabase,
      isTask,
      isTaskHoy: effectivePath.startsWith('/task'),
      isSettings: activeSetting !== null,
      isSettingsWorkspace: activeSetting === 'workspace',
      showMapTools: logicalPathname.startsWith('/maptools'),
      screen: screenFor(effectivePath, logicalPathname, activeSetting),
    }
  }, [logicalPathname, effectivePath, prefix, params, isDappView, activeSetting])
}
